/**
 * Schema validation tool based on DefinitionService.swift
 * This validates experience definitions against the Swift implementation's requirements
 */

export interface ValidationError {
    path: string;
    message: string;
    suggestion?: string;
    severity: 'error' | 'warning';
}

export interface ValidationResult {
    isValid: boolean;
    errors: ValidationError[];
    warnings: ValidationError[];
}

export class SchemaValidator {
    private static instance: SchemaValidator;

    public static getInstance(): SchemaValidator {
        if (!SchemaValidator.instance) {
            SchemaValidator.instance = new SchemaValidator();
        }
        return SchemaValidator.instance;
    }

    /**
     * Validates a complete experience definition
     */
    public validateDefinition(definition: any): ValidationResult {
        const errors: ValidationError[] = [];
        const warnings: ValidationError[] = [];

        if (!definition) {
            errors.push({
                path: 'root',
                message: 'Definition is null or undefined',
                suggestion: 'Ensure the definition object exists',
                severity: 'error',
            });
            return { isValid: false, errors, warnings };
        }

        // Validate top-level required fields based on PEExperienceDefinition
        this.validateTopLevel(definition, errors, warnings);

        // Validate screens array
        if (definition.screens) {
            this.validateScreens(definition.screens, errors, warnings);
        }

        // Validate processing flows
        if (definition.processingFlows) {
            this.validateProcessingFlows(
                definition.processingFlows,
                errors,
                warnings,
            );
        }

        // Validate sharing stations
        if (definition.sharing) {
            this.validateSharing(definition.sharing, errors, warnings);
        }

        return {
            isValid: errors.length === 0,
            errors,
            warnings,
        };
    }

    /**
     * Validates top-level definition fields
     */
    private validateTopLevel(
        definition: any,
        errors: ValidationError[],
        warnings: ValidationError[],
    ): void {
        // Required fields based on PEExperienceDefinition
        if (typeof definition.version !== 'number') {
            errors.push({
                path: 'version',
                message: 'version is required and must be a number',
                suggestion: 'Set version to 1',
                severity: 'error',
            });
        }

        if (typeof definition.definitionVersion !== 'number') {
            errors.push({
                path: 'definitionVersion',
                message: 'definitionVersion is required and must be a number',
                suggestion: 'Set definitionVersion to 1',
                severity: 'error',
            });
        }

        if (!Array.isArray(definition.screens)) {
            errors.push({
                path: 'screens',
                message: 'screens is required and must be an array',
                suggestion: 'Provide an array of screen definitions',
                severity: 'error',
            });
        }

        if (
            !definition.processingFlows ||
            typeof definition.processingFlows !== 'object'
        ) {
            errors.push({
                path: 'processingFlows',
                message: 'processingFlows is required and must be an object',
                suggestion:
                    'Provide an object with processing flow definitions',
                severity: 'error',
            });
        }

        // Optional fields validation
        if (
            definition.mirror !== undefined &&
            typeof definition.mirror !== 'boolean'
        ) {
            errors.push({
                path: 'mirror',
                message: 'mirror must be a boolean',
                severity: 'error',
            });
        }

        if (definition.exitPin !== undefined) {
            if (
                typeof definition.exitPin !== 'string' ||
                !/^\d{4}$/.test(definition.exitPin)
            ) {
                errors.push({
                    path: 'exitPin',
                    message: 'exitPin must be a 4-digit numeric string',
                    suggestion: 'Use format like "1234"',
                    severity: 'error',
                });
            }
        }
    }

    /**
     * Validates screens array
     */
    private validateScreens(
        screens: any[],
        errors: ValidationError[],
        warnings: ValidationError[],
    ): void {
        if (screens.length === 0) {
            warnings.push({
                path: 'screens',
                message: 'No screens defined',
                suggestion:
                    'Add at least one screen to create a functional experience',
                severity: 'warning',
            });
            return;
        }

        const screenIds = new Set<string>();

        screens.forEach((screen, index) => {
            const basePath = `screens[${index}]`;
            this.validateScreen(screen, basePath, errors, warnings, screenIds);
        });
    }

    /**
     * Validates a single screen based on PEScreen
     */
    private validateScreen(
        screen: any,
        basePath: string,
        errors: ValidationError[],
        warnings: ValidationError[],
        screenIds: Set<string>,
    ): void {
        if (!screen) {
            errors.push({
                path: basePath,
                message: 'Screen is null or undefined',
                severity: 'error',
            });
            return;
        }

        // Required: id (string)
        if (typeof screen.id !== 'string' || screen.id.trim() === '') {
            errors.push({
                path: `${basePath}.id`,
                message: 'Screen id is required and must be a non-empty string',
                suggestion: 'Provide a unique identifier for the screen',
                severity: 'error',
            });
        } else {
            if (screenIds.has(screen.id)) {
                errors.push({
                    path: `${basePath}.id`,
                    message: `Duplicate screen id: ${screen.id}`,
                    suggestion: 'Each screen must have a unique id',
                    severity: 'error',
                });
            } else {
                screenIds.add(screen.id);
            }
        }

        // Required: displayProperties
        if (!screen.displayProperties) {
            errors.push({
                path: `${basePath}.displayProperties`,
                message: 'displayProperties is required',
                suggestion:
                    'Add display properties with backgroundColor, backgroundMediaUrl, foregroundColor, buttonColor, buttonTextColor',
                severity: 'error',
            });
        } else {
            this.validateDisplayProperties(
                screen.displayProperties,
                `${basePath}.displayProperties`,
                errors,
                warnings,
            );
        }

        // Required: stage
        if (!screen.stage) {
            errors.push({
                path: `${basePath}.stage`,
                message: 'stage is required',
                suggestion: 'Add stage with type and metadata',
                severity: 'error',
            });
        } else {
            this.validateStage(
                screen.stage,
                `${basePath}.stage`,
                errors,
                warnings,
            );
        }

        // Required: elements (array)
        if (!Array.isArray(screen.elements)) {
            errors.push({
                path: `${basePath}.elements`,
                message: 'elements is required and must be an array',
                suggestion: 'Provide an array of UI elements (can be empty)',
                severity: 'error',
            });
        } else {
            this.validateElements(
                screen.elements,
                `${basePath}.elements`,
                errors,
                warnings,
            );
        }

        // Optional fields
        if (
            screen.navigateAfterTimeout !== undefined &&
            typeof screen.navigateAfterTimeout !== 'number'
        ) {
            errors.push({
                path: `${basePath}.navigateAfterTimeout`,
                message: 'navigateAfterTimeout must be a number',
                severity: 'error',
            });
        }

        if (
            screen.resetAfterTimeout !== undefined &&
            screen.resetAfterTimeout !== null &&
            typeof screen.resetAfterTimeout !== 'number'
        ) {
            errors.push({
                path: `${basePath}.resetAfterTimeout`,
                message: 'resetAfterTimeout must be a number or null',
                severity: 'error',
            });
        }

        if (
            screen.navigateDestination !== undefined &&
            typeof screen.navigateDestination !== 'string'
        ) {
            errors.push({
                path: `${basePath}.navigateDestination`,
                message: 'navigateDestination must be a string',
                severity: 'error',
            });
        }
    }

    /**
     * Validates display properties based on PEDisplayProperties
     */
    private validateDisplayProperties(
        props: any,
        basePath: string,
        errors: ValidationError[],
        warnings: ValidationError[],
    ): void {
        const required = [
            'backgroundColor',
            'backgroundMediaUrl',
            'foregroundColor',
            'buttonColor',
            'buttonTextColor',
        ];

        required.forEach((field) => {
            if (typeof props[field] !== 'string') {
                errors.push({
                    path: `${basePath}.${field}`,
                    message: `${field} is required and must be a string`,
                    suggestion: field.includes('Color')
                        ? 'Use hex color format like "#FFFFFF"'
                        : 'Provide a valid URL or empty string',
                    severity: 'error',
                });
            }
        });

        // Optional numeric fields
        ['horizontalPadding', 'verticalPadding'].forEach((field) => {
            if (
                props[field] !== undefined &&
                typeof props[field] !== 'number'
            ) {
                errors.push({
                    path: `${basePath}.${field}`,
                    message: `${field} must be a number`,
                    severity: 'error',
                });
            }
        });

        // Validate color formats
        [
            'backgroundColor',
            'foregroundColor',
            'buttonColor',
            'buttonTextColor',
        ].forEach((field) => {
            if (
                typeof props[field] === 'string' &&
                props[field] &&
                !props[field].match(/^#[0-9A-Fa-f]{6}$/)
            ) {
                warnings.push({
                    path: `${basePath}.${field}`,
                    message: `${field} should be a valid hex color`,
                    suggestion: 'Use format like "#FFFFFF"',
                    severity: 'warning',
                });
            }
        });
    }

    /**
     * Validates stage based on PEStageWrapper and metadata types
     */
    private validateStage(
        stage: any,
        basePath: string,
        errors: ValidationError[],
        warnings: ValidationError[],
    ): void {
        // Required: type
        const validStageTypes = [
            'attract',
            'capture',
            'countDown',
            'review',
            'share',
            'processing',
            'query',
        ];
        if (!validStageTypes.includes(stage.type)) {
            errors.push({
                path: `${basePath}.type`,
                message: `Invalid stage type: ${stage.type}`,
                suggestion: `Use one of: ${validStageTypes.join(', ')}`,
                severity: 'error',
            });
        }

        // Required: metadata
        if (!stage.metadata) {
            errors.push({
                path: `${basePath}.metadata`,
                message: 'stage metadata is required',
                suggestion:
                    'Add metadata object appropriate for the stage type',
                severity: 'error',
            });
        } else {
            this.validateStageMetadata(
                stage.metadata,
                stage.type,
                `${basePath}.metadata`,
                errors,
                warnings,
            );
        }
    }

    /**
     * Validates stage metadata based on type
     */
    private validateStageMetadata(
        metadata: any,
        stageType: string,
        basePath: string,
        errors: ValidationError[],
        warnings: ValidationError[],
    ): void {
        switch (stageType) {
            case 'capture':
                this.validateCaptureMetadata(
                    metadata,
                    basePath,
                    errors,
                    warnings,
                );
                break;
            case 'processing':
                this.validateProcessingMetadata(
                    metadata,
                    basePath,
                    errors,
                    warnings,
                );
                break;
            case 'query':
                this.validateQueryMetadata(
                    metadata,
                    basePath,
                    errors,
                    warnings,
                );
                break;
            default:
                // For attract, review, share stages, metadata can be empty object
                if (typeof metadata !== 'object') {
                    errors.push({
                        path: basePath,
                        message: 'metadata must be an object',
                        severity: 'error',
                    });
                }
                break;
        }
    }

    /**
     * Validates capture metadata based on PECaptureMetadata
     */
    private validateCaptureMetadata(
        metadata: any,
        basePath: string,
        errors: ValidationError[],
        warnings: ValidationError[],
    ): void {
        // Required fields based on init(from decoder:) in PECaptureMetadata
        const requiredFields = [
            'mode',
            'flowId',
            'shouldNavigateAfterCapture',
            'prompt',
        ];

        requiredFields.forEach((field) => {
            if (metadata[field] === undefined || metadata[field] === null) {
                errors.push({
                    path: `${basePath}.${field}`,
                    message: `${field} is required for capture stage`,
                    severity: 'error',
                });
            }
        });

        // Validate mode enum
        const validModes = [
            'clicker',
            'gif',
            'video',
            'tethered',
            'photo',
            'sequence',
        ];
        if (metadata.mode && !validModes.includes(metadata.mode)) {
            errors.push({
                path: `${basePath}.mode`,
                message: `Invalid capture mode: ${metadata.mode}`,
                suggestion: `Use one of: ${validModes.join(', ')}`,
                severity: 'error',
            });
        }

        // Validate flowId is number
        if (
            metadata.flowId !== undefined &&
            typeof metadata.flowId !== 'number'
        ) {
            errors.push({
                path: `${basePath}.flowId`,
                message: 'flowId must be a number',
                severity: 'error',
            });
        }

        // Validate boolean fields
        if (
            metadata.shouldNavigateAfterCapture !== undefined &&
            typeof metadata.shouldNavigateAfterCapture !== 'boolean'
        ) {
            errors.push({
                path: `${basePath}.shouldNavigateAfterCapture`,
                message: 'shouldNavigateAfterCapture must be a boolean',
                severity: 'error',
            });
        }

        // Validate string fields
        if (
            metadata.prompt !== undefined &&
            typeof metadata.prompt !== 'string'
        ) {
            errors.push({
                path: `${basePath}.prompt`,
                message: 'prompt must be a string',
                severity: 'error',
            });
        }

        // Optional fields with defaults (based on decodeIfPresent calls)
        const optionalNumericFields = [
            'countDown',
            'photoCount',
            'navigateAfterCaptureDelay',
            'gifLoopCount',
        ];
        optionalNumericFields.forEach((field) => {
            if (
                metadata[field] !== undefined &&
                typeof metadata[field] !== 'number'
            ) {
                errors.push({
                    path: `${basePath}.${field}`,
                    message: `${field} must be a number`,
                    severity: 'error',
                });
            }
        });

        const optionalBooleanFields = [
            'captureOnMount',
            'skipGifGeneration',
            'gifReverse',
            'hasNoTimeout',
            'takeFirstPhotoImmediately',
            'includeIndividualPhotos',
            'addToSharedPhotos',
            'replacePreviousCapture',
        ];
        optionalBooleanFields.forEach((field) => {
            if (
                metadata[field] !== undefined &&
                typeof metadata[field] !== 'boolean'
            ) {
                errors.push({
                    path: `${basePath}.${field}`,
                    message: `${field} must be a boolean`,
                    severity: 'error',
                });
            }
        });

        const optionalStringFields = [
            'processingMessage',
            'processingMessageFontFace',
            'captureMessage',
            'postCaptureDestination',
            'dynamicTextIdentifier',
            'postCaptureAction',
            'valueKey',
        ];
        optionalStringFields.forEach((field) => {
            if (
                metadata[field] !== undefined &&
                typeof metadata[field] !== 'string'
            ) {
                errors.push({
                    path: `${basePath}.${field}`,
                    message: `${field} must be a string`,
                    severity: 'error',
                });
            }
        });

        const optionalFloatFields = [
            'gifDuration',
            'gifCaptureDuration',
            'fps',
            'timeout',
            'processingMessageFontSize',
            'processingMessageFontWeight',
        ];
        optionalFloatFields.forEach((field) => {
            if (
                metadata[field] !== undefined &&
                typeof metadata[field] !== 'number'
            ) {
                errors.push({
                    path: `${basePath}.${field}`,
                    message: `${field} must be a number`,
                    severity: 'error',
                });
            }
        });

        this.validateGifLoopCount(metadata, basePath, errors);
    }

    /**
     * `gifLoopCount` is decoded as an Int in Swift, so fractional values would
     * fail decoding, and a loop count below 1 would produce an empty GIF.
     */
    private validateGifLoopCount(
        metadata: any,
        basePath: string,
        errors: ValidationError[],
    ): void {
        if (
            metadata.gifLoopCount !== undefined &&
            typeof metadata.gifLoopCount === 'number' &&
            (!Number.isInteger(metadata.gifLoopCount) ||
                metadata.gifLoopCount < 1)
        ) {
            errors.push({
                path: `${basePath}.gifLoopCount`,
                message: 'gifLoopCount must be a whole number of at least 1',
                severity: 'error',
            });
        }
    }

    /**
     * Validates processing metadata based on PEProcessingMetadata
     */
    private validateProcessingMetadata(
        metadata: any,
        basePath: string,
        errors: ValidationError[],
        warnings: ValidationError[],
    ): void {
        // Required fields based on init(from decoder:) in PEProcessingMetadata.
        // `type`, `flowId`, and `shouldNavigateAfterProcessing` are always
        // required; the remaining fields depend on the processing type.
        const requiredFields = [
            'type',
            'flowId',
            'shouldNavigateAfterProcessing',
        ];

        if (metadata.type === 'gif') {
            requiredFields.push('gifInputs');
        } else {
            // Composite layout requirements.
            requiredFields.push(
                'inputs',
                'height',
                'width',
                'expectedPhotoCount',
                'processingPhotoInstructions',
            );
        }

        requiredFields.forEach((field) => {
            if (metadata[field] === undefined || metadata[field] === null) {
                errors.push({
                    path: `${basePath}.${field}`,
                    message: `${field} is required for processing stage`,
                    severity: 'error',
                });
            }
        });

        // Validate type enum
        if (
            metadata.type &&
            metadata.type !== 'composite' &&
            metadata.type !== 'gif'
        ) {
            errors.push({
                path: `${basePath}.type`,
                message: `Invalid processing type: ${metadata.type}`,
                suggestion: 'Supported types are "composite" and "gif"',
                severity: 'error',
            });
        }

        // Validate gifInputs shape (only used by the `gif` processing type)
        if (metadata.gifInputs !== undefined) {
            if (!Array.isArray(metadata.gifInputs)) {
                errors.push({
                    path: `${basePath}.gifInputs`,
                    message: 'gifInputs must be an array',
                    severity: 'error',
                });
            } else {
                metadata.gifInputs.forEach((entry: any, index: number) => {
                    if (!entry || typeof entry.input !== 'string') {
                        errors.push({
                            path: `${basePath}.gifInputs[${index}].input`,
                            message: 'gifInputs entry requires a string "input"',
                            severity: 'error',
                        });
                    }
                    if (
                        entry &&
                        entry.flowId !== undefined &&
                        entry.flowId !== null &&
                        typeof entry.flowId !== 'number'
                    ) {
                        errors.push({
                            path: `${basePath}.gifInputs[${index}].flowId`,
                            message: 'gifInputs entry flowId must be a number',
                            severity: 'error',
                        });
                    }
                });
            }
        }

        // Validate inputs is array
        if (metadata.inputs && !Array.isArray(metadata.inputs)) {
            errors.push({
                path: `${basePath}.inputs`,
                message: 'inputs must be an array of screen IDs',
                severity: 'error',
            });
        }

        // Validate processingPhotoInstructions is array
        if (
            metadata.processingPhotoInstructions &&
            !Array.isArray(metadata.processingPhotoInstructions)
        ) {
            errors.push({
                path: `${basePath}.processingPhotoInstructions`,
                message: 'processingPhotoInstructions must be an array',
                severity: 'error',
            });
        } else if (Array.isArray(metadata.processingPhotoInstructions)) {
            metadata.processingPhotoInstructions.forEach(
                (instruction: any, index: number) => {
                    this.validateCompositePhotoProcessingInstruction(
                        instruction,
                        `${basePath}.processingPhotoInstructions[${index}]`,
                        errors,
                        warnings,
                    );
                },
            );
        }

        // Validate numeric fields
        ['flowId', 'expectedPhotoCount', 'height', 'width'].forEach((field) => {
            if (
                metadata[field] !== undefined &&
                typeof metadata[field] !== 'number'
            ) {
                errors.push({
                    path: `${basePath}.${field}`,
                    message: `${field} must be a number`,
                    severity: 'error',
                });
            }
        });

        // Validate optional GIF float fields (only used by the `gif` processing type)
        ['gifDuration', 'fps', 'gifLoopCount'].forEach((field) => {
            if (
                metadata[field] !== undefined &&
                typeof metadata[field] !== 'number'
            ) {
                errors.push({
                    path: `${basePath}.${field}`,
                    message: `${field} must be a number`,
                    severity: 'error',
                });
            }
        });

        this.validateGifLoopCount(metadata, basePath, errors);

        if (
            metadata.gifReverse !== undefined &&
            typeof metadata.gifReverse !== 'boolean'
        ) {
            errors.push({
                path: `${basePath}.gifReverse`,
                message: 'gifReverse must be a boolean',
                severity: 'error',
            });
        }

        // Validate boolean fields
        if (
            metadata.shouldNavigateAfterProcessing !== undefined &&
            typeof metadata.shouldNavigateAfterProcessing !== 'boolean'
        ) {
            errors.push({
                path: `${basePath}.shouldNavigateAfterProcessing`,
                message: 'shouldNavigateAfterProcessing must be a boolean',
                severity: 'error',
            });
        }

        if (
            metadata.autoPrint !== undefined &&
            typeof metadata.autoPrint !== 'boolean'
        ) {
            errors.push({
                path: `${basePath}.autoPrint`,
                message: 'autoPrint must be a boolean',
                severity: 'error',
            });
        }

        // Validate optional composite card-border fields (only used by the
        // `composite` processing type).
        if (
            metadata.cardBorderColor !== undefined &&
            typeof metadata.cardBorderColor !== 'string'
        ) {
            errors.push({
                path: `${basePath}.cardBorderColor`,
                message: 'cardBorderColor must be a string',
                severity: 'error',
            });
        }

        if (
            metadata.cardBorderWidth !== undefined &&
            typeof metadata.cardBorderWidth !== 'number'
        ) {
            errors.push({
                path: `${basePath}.cardBorderWidth`,
                message: 'cardBorderWidth must be a number',
                severity: 'error',
            });
        }

        this.validateAttributionStrip(metadata, basePath, errors);
    }

    /**
     * Validates the optional print-attribution strip on a composite
     * (PEAttributionStrip). `source` is the only required field (a
     * non-optional String in Swift); the rest are type-checked when present.
     */
    private validateAttributionStrip(
        metadata: any,
        basePath: string,
        errors: ValidationError[],
    ): void {
        if (metadata.attributionStrip === undefined) {
            return;
        }

        const strip = metadata.attributionStrip;
        const stripPath = `${basePath}.attributionStrip`;

        if (
            typeof strip !== 'object' ||
            strip === null ||
            Array.isArray(strip)
        ) {
            errors.push({
                path: stripPath,
                message: 'attributionStrip must be an object',
                severity: 'error',
            });
            return;
        }

        if (typeof strip.source !== 'string') {
            errors.push({
                path: `${stripPath}.source`,
                message:
                    'attributionStrip.source is required and must be a string',
                severity: 'error',
            });
        }

        if (
            strip.edge !== undefined &&
            strip.edge !== 'left' &&
            strip.edge !== 'right' &&
            strip.edge !== 'bottom'
        ) {
            errors.push({
                path: `${stripPath}.edge`,
                message:
                    'attributionStrip.edge must be "left", "right", or "bottom"',
                severity: 'error',
            });
        }

        ['fontFace', 'color', 'backgroundColor'].forEach((field) => {
            if (
                strip[field] !== undefined &&
                typeof strip[field] !== 'string'
            ) {
                errors.push({
                    path: `${stripPath}.${field}`,
                    message: `attributionStrip.${field} must be a string`,
                    severity: 'error',
                });
            }
        });

        ['width', 'fontSize'].forEach((field) => {
            if (
                strip[field] !== undefined &&
                typeof strip[field] !== 'number'
            ) {
                errors.push({
                    path: `${stripPath}.${field}`,
                    message: `attributionStrip.${field} must be a number`,
                    severity: 'error',
                });
            }
        });
    }

    /**
     * Validates composite photo processing instruction
     */
    private validateCompositePhotoProcessingInstruction(
        instruction: any,
        basePath: string,
        errors: ValidationError[],
        warnings: ValidationError[],
    ): void {
        const requiredFields = ['x', 'y', 'width', 'height'];

        requiredFields.forEach((field) => {
            if (typeof instruction[field] !== 'number') {
                errors.push({
                    path: `${basePath}.${field}`,
                    message: `${field} is required and must be a number`,
                    severity: 'error',
                });
            }
        });

        // Optional flowId
        if (
            instruction.flowId !== undefined &&
            typeof instruction.flowId !== 'number'
        ) {
            errors.push({
                path: `${basePath}.flowId`,
                message: 'flowId must be a number',
                severity: 'error',
            });
        }
    }

    /**
     * Validates query metadata based on PEQueryMetadata
     */
    private validateQueryMetadata(
        metadata: any,
        basePath: string,
        errors: ValidationError[],
        warnings: ValidationError[],
    ): void {
        // Required fields
        if (!Array.isArray(metadata.descriptors)) {
            errors.push({
                path: `${basePath}.descriptors`,
                message: 'descriptors is required and must be an array',
                severity: 'error',
            });
        }

        if (typeof metadata.destination !== 'string') {
            errors.push({
                path: `${basePath}.destination`,
                message: 'destination is required and must be a string',
                severity: 'error',
            });
        }
    }

    /**
     * Validates UI elements array
     */
    private validateElements(
        elements: any[],
        basePath: string,
        errors: ValidationError[],
        warnings: ValidationError[],
    ): void {
        const elementIds = new Set<string>();

        elements.forEach((element, index) => {
            const elementPath = `${basePath}[${index}]`;
            this.validateUIElement(
                element,
                elementPath,
                errors,
                warnings,
                elementIds,
            );
        });
    }

    /**
     * Validates a single UI element based on PEUIElementWrapper
     */
    private validateUIElement(
        element: any,
        basePath: string,
        errors: ValidationError[],
        warnings: ValidationError[],
        elementIds: Set<string>,
    ): void {
        if (!element) {
            errors.push({
                path: basePath,
                message: 'Element is null or undefined',
                severity: 'error',
            });
            return;
        }

        // Required: id
        if (typeof element.id !== 'string' || element.id.trim() === '') {
            errors.push({
                path: `${basePath}.id`,
                message:
                    'Element id is required and must be a non-empty string',
                severity: 'error',
            });
        } else {
            if (elementIds.has(element.id)) {
                errors.push({
                    path: `${basePath}.id`,
                    message: `Duplicate element id: ${element.id}`,
                    suggestion: 'Each element must have a unique id',
                    severity: 'error',
                });
            } else {
                elementIds.add(element.id);
            }
        }

        // Required: order
        if (typeof element.order !== 'number') {
            errors.push({
                path: `${basePath}.order`,
                message: 'order is required and must be a number',
                severity: 'error',
            });
        }

        // Required: type
        const validTypes = [
            'text',
            'image',
            'video',
            'button',
            'input',
            'outputPreview',
            'dynamicText',
            'currentSessionQR',
            'galleryQR',
            'paymentQR',
            'galleryGrid',
            'sessionGrid',
            'mediaGrid',
            'liveView',
            'selectedQR',
            'codeScanner',
            'group',
            'artworkSelector',
            'phoneKeypad',
        ];
        if (!validTypes.includes(element.type)) {
            errors.push({
                path: `${basePath}.type`,
                message: `Invalid element type: ${element.type}`,
                suggestion: `Use one of: ${validTypes.join(', ')}`,
                severity: 'error',
            });
        }

        // Required: metadata
        if (!element.metadata) {
            errors.push({
                path: `${basePath}.metadata`,
                message: 'metadata is required',
                severity: 'error',
            });
        } else {
            this.validateElementMetadata(
                element.metadata,
                element.type,
                `${basePath}.metadata`,
                errors,
                warnings,
            );
        }

        // Optional fields with validation
        if (element.horizontalLocation !== undefined) {
            const validHorizontal = ['leading', 'center', 'trailing'];
            if (!validHorizontal.includes(element.horizontalLocation)) {
                errors.push({
                    path: `${basePath}.horizontalLocation`,
                    message: `Invalid horizontalLocation: ${element.horizontalLocation}`,
                    suggestion: `Use one of: ${validHorizontal.join(', ')}`,
                    severity: 'error',
                });
            }
        }

        if (element.verticalLocation !== undefined) {
            const validVertical = ['top', 'center', 'bottom'];
            if (!validVertical.includes(element.verticalLocation)) {
                errors.push({
                    path: `${basePath}.verticalLocation`,
                    message: `Invalid verticalLocation: ${element.verticalLocation}`,
                    suggestion: `Use one of: ${validVertical.join(', ')}`,
                    severity: 'error',
                });
            }
        }

        if (element.layer !== undefined) {
            const validLayers = [0, 1, 2];
            if (!validLayers.includes(element.layer)) {
                errors.push({
                    path: `${basePath}.layer`,
                    message: `Invalid layer: ${element.layer}`,
                    suggestion: `Use one of: ${validLayers.join(', ')}`,
                    severity: 'error',
                });
            }
        }

        // Validate positioning - element must have either grid position OR group location
        const hasGridPosition =
            element.horizontalLocation && element.verticalLocation;
        const hasGroupLocation = element.groupLocation;

        if (!hasGridPosition && !hasGroupLocation) {
            warnings.push({
                path: basePath,
                message:
                    'Element should have either grid position (horizontalLocation + verticalLocation) or groupLocation',
                suggestion:
                    'Set horizontalLocation and verticalLocation, or place element in a group',
                severity: 'warning',
            });
        }
    }

    /**
     * Validates element metadata based on type
     */
    private validateElementMetadata(
        metadata: any,
        elementType: string,
        basePath: string,
        errors: ValidationError[],
        warnings: ValidationError[],
    ): void {
        // All metadata should have type field matching element type
        if (metadata.type !== elementType) {
            errors.push({
                path: `${basePath}.type`,
                message: `Metadata type "${metadata.type}" doesn't match element type "${elementType}"`,
                severity: 'error',
            });
        }

        // Type-specific validation based on Swift metadata classes
        switch (elementType) {
            case 'text':
                this.validateTextMetadata(metadata, basePath, errors, warnings);
                break;
            case 'button':
                this.validateButtonMetadata(
                    metadata,
                    basePath,
                    errors,
                    warnings,
                );
                break;
            case 'image':
                this.validateImageMetadata(
                    metadata,
                    basePath,
                    errors,
                    warnings,
                );
                break;
            case 'input':
                this.validateInputMetadata(
                    metadata,
                    basePath,
                    errors,
                    warnings,
                );
                break;
            case 'dynamicText':
                this.validateDynamicTextMetadata(
                    metadata,
                    basePath,
                    errors,
                    warnings,
                );
                break;
            case 'currentSessionQR':
            case 'galleryQR':
            case 'selectedQR':
                this.validateQRMetadata(metadata, basePath, errors, warnings);
                break;
            case 'paymentQR':
                this.validatePaymentQRMetadata(
                    metadata,
                    basePath,
                    errors,
                    warnings,
                );
                break;
            case 'outputPreview':
                this.validateOutputPreviewMetadata(
                    metadata,
                    basePath,
                    errors,
                    warnings,
                );
                break;
            case 'liveView':
                this.validateLiveViewMetadata(
                    metadata,
                    basePath,
                    errors,
                    warnings,
                );
                break;
            case 'galleryGrid':
            case 'sessionGrid':
            case 'mediaGrid':
                this.validateGridMetadata(metadata, basePath, errors, warnings);
                break;
            case 'group':
                this.validateGroupMetadata(
                    metadata,
                    basePath,
                    errors,
                    warnings,
                );
                break;
            case 'artworkSelector':
                this.validateArtworkSelectorMetadata(
                    metadata,
                    basePath,
                    errors,
                    warnings,
                );
                break;
            case 'codeScanner':
                this.validateCodeScannerMetadata(
                    metadata,
                    basePath,
                    errors,
                    warnings,
                );
                break;
            case 'phoneKeypad':
                this.validatePhoneKeypadMetadata(
                    metadata,
                    basePath,
                    errors,
                    warnings,
                );
                break;
        }
    }

    /**
     * Validates text metadata based on PEUITextMetadata
     */
    private validateTextMetadata(
        metadata: any,
        basePath: string,
        errors: ValidationError[],
        warnings: ValidationError[],
    ): void {
        // Required fields based on init(from decoder:)
        if (typeof metadata.text !== 'string') {
            errors.push({
                path: `${basePath}.text`,
                message: 'text is required and must be a string',
                severity: 'error',
            });
        }

        if (typeof metadata.fontSize !== 'number') {
            errors.push({
                path: `${basePath}.fontSize`,
                message: 'fontSize is required and must be a number',
                severity: 'error',
            });
        }

        // Optional fields with defaults
        const optionalNumericFields = [
            'fontWeight',
            'horizontalMargin',
            'verticalMargin',
            'verticalOffset',
            'horizontalOffset',
        ];
        optionalNumericFields.forEach((field) => {
            if (
                metadata[field] !== undefined &&
                typeof metadata[field] !== 'number'
            ) {
                errors.push({
                    path: `${basePath}.${field}`,
                    message: `${field} must be a number`,
                    severity: 'error',
                });
            }
        });

        if (
            metadata.fontFace !== undefined &&
            typeof metadata.fontFace !== 'string'
        ) {
            errors.push({
                path: `${basePath}.fontFace`,
                message: 'fontFace must be a string',
                severity: 'error',
            });
        }

        if (
            metadata.hideWhenKeyboardVisible !== undefined &&
            typeof metadata.hideWhenKeyboardVisible !== 'boolean'
        ) {
            errors.push({
                path: `${basePath}.hideWhenKeyboardVisible`,
                message: 'hideWhenKeyboardVisible must be a boolean',
                severity: 'error',
            });
        }

        if (metadata.alignment !== undefined) {
            const validAlignments = ['center', 'leading', 'trailing'];
            if (!validAlignments.includes(metadata.alignment)) {
                errors.push({
                    path: `${basePath}.alignment`,
                    message: `Invalid alignment: ${metadata.alignment}`,
                    suggestion: `Use one of: ${validAlignments.join(', ')}`,
                    severity: 'error',
                });
            }
        }
    }

    /**
     * Validates button metadata based on PEUIButtonMetadata
     */
    private validateButtonMetadata(
        metadata: any,
        basePath: string,
        errors: ValidationError[],
        warnings: ValidationError[],
    ): void {
        // Most fields are optional with defaults in PEUIButtonMetadata

        // Validate action field (can be string or array)
        if (metadata.action !== undefined) {
            const validActions = [
                'capture',
                'reset',
                'navigate',
                'setValue',
                'appendValue',
                'share',
                'print',
                'gallery',
                'shareAndReset',
                'shareGallery',
                'clearGallerySelection',
                'shareAndClearGallery',
                'searchShares',
                'setNextProcessingFlow',
                'multiSelectPrint',
                'selectArtwork',
            ];

            if (typeof metadata.action === 'string') {
                if (!validActions.includes(metadata.action)) {
                    errors.push({
                        path: `${basePath}.action`,
                        message: `Invalid action: ${metadata.action}`,
                        suggestion: `Use one of: ${validActions.join(', ')}`,
                        severity: 'error',
                    });
                }
            } else if (Array.isArray(metadata.action)) {
                metadata.action.forEach((action: any, index: number) => {
                    if (!validActions.includes(action)) {
                        errors.push({
                            path: `${basePath}.action[${index}]`,
                            message: `Invalid action: ${action}`,
                            suggestion: `Use one of: ${validActions.join(
                                ', ',
                            )}`,
                            severity: 'error',
                        });
                    }
                });
            } else {
                errors.push({
                    path: `${basePath}.action`,
                    message: 'action must be a string or array of strings',
                    severity: 'error',
                });
            }
        }

        // Optional string fields
        const optionalStringFields = [
            'text',
            'destination',
            'imageUrl',
            'value',
            'valueKey',
            'delimiter',
            'searchKey',
            'borderColor',
            'fontFace',
            'keyPressTrigger',
            'fontColor',
            'backgroundColor',
        ];
        optionalStringFields.forEach((field) => {
            if (
                metadata[field] !== undefined &&
                typeof metadata[field] !== 'string'
            ) {
                errors.push({
                    path: `${basePath}.${field}`,
                    message: `${field} must be a string`,
                    severity: 'error',
                });
            }
        });

        // Optional numeric fields
        const optionalNumericFields = [
            'fontSize',
            'fontWeight',
            'borderSize',
            'horizontalPadding',
            'verticalPadding',
            'horizontalMargin',
            'verticalMargin',
            'cornerRadius',
            'verticalOffset',
            'horizontalOffset',
            'width',
            'height',
        ];
        optionalNumericFields.forEach((field) => {
            if (
                metadata[field] !== undefined &&
                typeof metadata[field] !== 'number'
            ) {
                errors.push({
                    path: `${basePath}.${field}`,
                    message: `${field} must be a number`,
                    severity: 'error',
                });
            }
        });

        // Optional boolean fields
        const optionalBooleanFields = [
            'volumeUpTrigger',
            'preventSend',
            'hideWhenKeyboardVisible',
            'dedup',
        ];
        optionalBooleanFields.forEach((field) => {
            if (
                metadata[field] !== undefined &&
                typeof metadata[field] !== 'boolean'
            ) {
                errors.push({
                    path: `${basePath}.${field}`,
                    message: `${field} must be a boolean`,
                    severity: 'error',
                });
            }
        });

        // Validate formatter enum
        if (metadata.formatter !== undefined) {
            const validFormatters = ['none', 'email', 'phoneNumber'];
            if (!validFormatters.includes(metadata.formatter)) {
                errors.push({
                    path: `${basePath}.formatter`,
                    message: `Invalid formatter: ${metadata.formatter}`,
                    suggestion: `Use one of: ${validFormatters.join(', ')}`,
                    severity: 'error',
                });
            }
        }
    }

    /**
     * Validates image metadata based on PEUIImageMetadata
     */
    private validateImageMetadata(
        metadata: any,
        basePath: string,
        errors: ValidationError[],
        warnings: ValidationError[],
    ): void {
        // Required fields based on init(from decoder:)
        const requiredNumericFields = [
            'height',
            'width',
            'verticalOffset',
            'horizontalOffset',
            'cornerRadius',
        ];
        requiredNumericFields.forEach((field) => {
            if (typeof metadata[field] !== 'number') {
                errors.push({
                    path: `${basePath}.${field}`,
                    message: `${field} is required and must be a number`,
                    severity: 'error',
                });
            }
        });

        // Optional fields
        if (
            metadata.imageUrl !== undefined &&
            typeof metadata.imageUrl !== 'string'
        ) {
            errors.push({
                path: `${basePath}.imageUrl`,
                message: 'imageUrl must be a string',
                severity: 'error',
            });
        }

        if (
            metadata.useImageDimensions !== undefined &&
            typeof metadata.useImageDimensions !== 'boolean'
        ) {
            errors.push({
                path: `${basePath}.useImageDimensions`,
                message: 'useImageDimensions must be a boolean',
                severity: 'error',
            });
        }
    }

    /**
     * Validates input metadata based on PEUIInputMetadata
     */
    private validateInputMetadata(
        metadata: any,
        basePath: string,
        errors: ValidationError[],
        warnings: ValidationError[],
    ): void {
        // Required fields
        if (typeof metadata.key !== 'string') {
            errors.push({
                path: `${basePath}.key`,
                message: 'key is required and must be a string',
                severity: 'error',
            });
        }

        if (typeof metadata.fontSize !== 'number') {
            errors.push({
                path: `${basePath}.fontSize`,
                message: 'fontSize is required and must be a number',
                severity: 'error',
            });
        }

        const validInputTypes = ['text', 'number', 'bool', 'phone', 'email'];
        if (!validInputTypes.includes(metadata.inputType)) {
            errors.push({
                path: `${basePath}.inputType`,
                message: `Invalid inputType: ${metadata.inputType}`,
                suggestion: `Use one of: ${validInputTypes.join(', ')}`,
                severity: 'error',
            });
        }

        // Optional fields with defaults
        const optionalNumericFields = [
            'width',
            'cornerRadius',
            'fontWeight',
            'verticalOffset',
            'horizontalOffset',
            'horizontalPadding',
            'verticalPadding',
            'borderSize',
        ];
        optionalNumericFields.forEach((field) => {
            if (
                metadata[field] !== undefined &&
                typeof metadata[field] !== 'number'
            ) {
                errors.push({
                    path: `${basePath}.${field}`,
                    message: `${field} must be a number`,
                    severity: 'error',
                });
            }
        });

        const optionalStringFields = [
            'label',
            'fontFace',
            'defaultValue',
            'placeholder',
            'borderColor',
        ];
        optionalStringFields.forEach((field) => {
            if (
                metadata[field] !== undefined &&
                typeof metadata[field] !== 'string'
            ) {
                errors.push({
                    path: `${basePath}.${field}`,
                    message: `${field} must be a string`,
                    severity: 'error',
                });
            }
        });

        if (
            metadata.isFocused !== undefined &&
            typeof metadata.isFocused !== 'boolean'
        ) {
            errors.push({
                path: `${basePath}.isFocused`,
                message: 'isFocused must be a boolean',
                severity: 'error',
            });
        }

        // Validate style enum
        if (metadata.style !== undefined) {
            const validStyles = ['roundedBox', 'line'];
            if (!validStyles.includes(metadata.style)) {
                errors.push({
                    path: `${basePath}.style`,
                    message: `Invalid style: ${metadata.style}`,
                    suggestion: `Use one of: ${validStyles.join(', ')}`,
                    severity: 'error',
                });
            }
        }

        // Validate alignment enum
        if (metadata.alignment !== undefined) {
            const validAlignments = ['center', 'leading', 'trailing'];
            if (!validAlignments.includes(metadata.alignment)) {
                errors.push({
                    path: `${basePath}.alignment`,
                    message: `Invalid alignment: ${metadata.alignment}`,
                    suggestion: `Use one of: ${validAlignments.join(', ')}`,
                    severity: 'error',
                });
            }
        }
    }

    /**
     * Validates dynamic text metadata based on PEUIDynamicTextMetadata
     */
    private validateDynamicTextMetadata(
        metadata: any,
        basePath: string,
        errors: ValidationError[],
        warnings: ValidationError[],
    ): void {
        // Required fields
        if (typeof metadata.fontSize !== 'number') {
            errors.push({
                path: `${basePath}.fontSize`,
                message: 'fontSize is required and must be a number',
                severity: 'error',
            });
        }

        if (typeof metadata.identifier !== 'string') {
            errors.push({
                path: `${basePath}.identifier`,
                message: 'identifier is required and must be a string',
                severity: 'error',
            });
        }

        // Validate action field (can be string or array)
        if (metadata.action !== undefined) {
            if (typeof metadata.action === 'string') {
                // Single action - validate it's in array format in Swift
            } else if (Array.isArray(metadata.action)) {
                // Array of actions
            } else {
                errors.push({
                    path: `${basePath}.action`,
                    message: 'action must be a string or array of strings',
                    severity: 'error',
                });
            }
        }

        // Optional fields similar to text metadata
        const optionalNumericFields = [
            'fontWeight',
            'horizontalMargin',
            'verticalMargin',
            'verticalOffset',
            'horizontalOffset',
        ];
        optionalNumericFields.forEach((field) => {
            if (
                metadata[field] !== undefined &&
                typeof metadata[field] !== 'number'
            ) {
                errors.push({
                    path: `${basePath}.${field}`,
                    message: `${field} must be a number`,
                    severity: 'error',
                });
            }
        });

        if (
            metadata.fontFace !== undefined &&
            typeof metadata.fontFace !== 'string'
        ) {
            errors.push({
                path: `${basePath}.fontFace`,
                message: 'fontFace must be a string',
                severity: 'error',
            });
        }

        if (metadata.alignment !== undefined) {
            const validAlignments = ['center', 'leading', 'trailing'];
            if (!validAlignments.includes(metadata.alignment)) {
                errors.push({
                    path: `${basePath}.alignment`,
                    message: `Invalid alignment: ${metadata.alignment}`,
                    suggestion: `Use one of: ${validAlignments.join(', ')}`,
                    severity: 'error',
                });
            }
        }
    }

    /**
     * Validates QR metadata based on PEUIQRMetadata
     */
    private validateQRMetadata(
        metadata: any,
        basePath: string,
        errors: ValidationError[],
        warnings: ValidationError[],
    ): void {
        // Required fields
        const requiredNumericFields = [
            'height',
            'width',
            'horizontalMargin',
            'verticalMargin',
        ];
        requiredNumericFields.forEach((field) => {
            if (typeof metadata[field] !== 'number') {
                errors.push({
                    path: `${basePath}.${field}`,
                    message: `${field} is required and must be a number`,
                    severity: 'error',
                });
            }
        });

        // Optional fields with defaults
        const optionalNumericFields = ['verticalOffset', 'horizontalOffset'];
        optionalNumericFields.forEach((field) => {
            if (
                metadata[field] !== undefined &&
                typeof metadata[field] !== 'number'
            ) {
                errors.push({
                    path: `${basePath}.${field}`,
                    message: `${field} must be a number`,
                    severity: 'error',
                });
            }
        });

        const optionalStringFields = ['foregroundColor', 'backgroundColor'];
        optionalStringFields.forEach((field) => {
            if (
                metadata[field] !== undefined &&
                typeof metadata[field] !== 'string'
            ) {
                errors.push({
                    path: `${basePath}.${field}`,
                    message: `${field} must be a string`,
                    severity: 'error',
                });
            }
        });
    }

    /**
     * Validates payment QR metadata based on PEUIPaymentQRMetadata
     */
    private validatePaymentQRMetadata(
        metadata: any,
        basePath: string,
        errors: ValidationError[],
        warnings: ValidationError[],
    ): void {
        // Required fields
        const requiredNumericFields = [
            'height',
            'width',
            'horizontalMargin',
            'verticalMargin',
        ];
        requiredNumericFields.forEach((field) => {
            if (typeof metadata[field] !== 'number') {
                errors.push({
                    path: `${basePath}.${field}`,
                    message: `${field} is required and must be a number`,
                    severity: 'error',
                });
            }
        });

        if (typeof metadata.destination !== 'string') {
            errors.push({
                path: `${basePath}.destination`,
                message: 'destination is required and must be a string',
                severity: 'error',
            });
        }

        // Optional numeric fields
        const optionalNumericFields = [
            'verticalOffset',
            'horizontalOffset',
            'customPrice',
            'fontSize',
            'fontWeight',
        ];
        optionalNumericFields.forEach((field) => {
            if (
                metadata[field] !== undefined &&
                typeof metadata[field] !== 'number'
            ) {
                errors.push({
                    path: `${basePath}.${field}`,
                    message: `${field} must be a number`,
                    severity: 'error',
                });
            }
        });

        // Optional string fields
        const optionalStringFields = [
            'foregroundColor',
            'backgroundColor',
            'fontFace',
        ];
        optionalStringFields.forEach((field) => {
            if (
                metadata[field] !== undefined &&
                typeof metadata[field] !== 'string'
            ) {
                errors.push({
                    path: `${basePath}.${field}`,
                    message: `${field} must be a string`,
                    severity: 'error',
                });
            }
        });

        // Validate color formats
        const colorFields = ['foregroundColor', 'backgroundColor'];
        colorFields.forEach((field) => {
            if (
                metadata[field] &&
                typeof metadata[field] === 'string' &&
                !metadata[field].match(/^#[0-9A-Fa-f]{6}$/)
            ) {
                warnings.push({
                    path: `${basePath}.${field}`,
                    message: `${field} should be a valid hex color (e.g., #FFFFFF)`,
                    severity: 'warning',
                });
            }
        });
    }

    /**
     * Validates output preview metadata based on PEUIOutputPreviewMetadata
     */
    private validateOutputPreviewMetadata(
        metadata: any,
        basePath: string,
        errors: ValidationError[],
        warnings: ValidationError[],
    ): void {
        // Required fields
        const requiredNumericFields = ['height', 'width'];
        requiredNumericFields.forEach((field) => {
            if (typeof metadata[field] !== 'number') {
                errors.push({
                    path: `${basePath}.${field}`,
                    message: `${field} is required and must be a number`,
                    severity: 'error',
                });
            }
        });

        // Optional fields
        const optionalNumericFields = [
            'verticalOffset',
            'horizontalOffset',
            'cornerRadius',
            'rotation',
        ];
        optionalNumericFields.forEach((field) => {
            if (
                metadata[field] !== undefined &&
                typeof metadata[field] !== 'number'
            ) {
                errors.push({
                    path: `${basePath}.${field}`,
                    message: `${field} must be a number`,
                    severity: 'error',
                });
            }
        });

        if (
            metadata.scene !== undefined &&
            typeof metadata.scene !== 'string'
        ) {
            errors.push({
                path: `${basePath}.scene`,
                message: 'scene must be a string',
                severity: 'error',
            });
        }
    }

    /**
     * Validates live view metadata based on PEUIElementLiveViewMetadata
     */
    private validateLiveViewMetadata(
        metadata: any,
        basePath: string,
        errors: ValidationError[],
        warnings: ValidationError[],
    ): void {
        // Required fields (with defaults)
        const optionalNumericFields = [
            'width',
            'height',
            'verticalOffset',
            'horizontalOffset',
            'cornerRadius',
            'flowId',
        ];
        optionalNumericFields.forEach((field) => {
            if (
                metadata[field] !== undefined &&
                typeof metadata[field] !== 'number'
            ) {
                errors.push({
                    path: `${basePath}.${field}`,
                    message: `${field} must be a number`,
                    severity: 'error',
                });
            }
        });

        if (
            metadata.showControls !== undefined &&
            typeof metadata.showControls !== 'boolean'
        ) {
            errors.push({
                path: `${basePath}.showControls`,
                message: 'showControls must be a boolean',
                severity: 'error',
            });
        }
    }

    /**
     * Validates grid metadata based on PEUIGridMetadata
     */
    private validateGridMetadata(
        metadata: any,
        basePath: string,
        errors: ValidationError[],
        warnings: ValidationError[],
    ): void {
        // Optional fields with defaults
        const optionalNumericFields = [
            'columns',
            'spacing',
            'height',
            'width',
            'horizontalMargin',
            'verticalMargin',
            'verticalOffset',
            'horizontalOffset',
            'cornerRadius',
            'maximumSelectionCount',
        ];
        optionalNumericFields.forEach((field) => {
            if (
                metadata[field] !== undefined &&
                typeof metadata[field] !== 'number'
            ) {
                errors.push({
                    path: `${basePath}.${field}`,
                    message: `${field} must be a number`,
                    severity: 'error',
                });
            }
        });

        const optionalStringFields = ['selectionColor', 'valueKey'];
        optionalStringFields.forEach((field) => {
            if (
                metadata[field] !== undefined &&
                typeof metadata[field] !== 'string'
            ) {
                errors.push({
                    path: `${basePath}.${field}`,
                    message: `${field} must be a string`,
                    severity: 'error',
                });
            }
        });
    }

    /**
     * Validates group metadata based on PEUIElementGroupMetadata
     */
    private validateGroupMetadata(
        metadata: any,
        basePath: string,
        errors: ValidationError[],
        warnings: ValidationError[],
    ): void {
        // Required fields
        if (typeof metadata.name !== 'string') {
            errors.push({
                path: `${basePath}.name`,
                message: 'name is required and must be a string',
                severity: 'error',
            });
        }

        // Optional fields with defaults
        if (metadata.direction !== undefined) {
            const validDirections = ['column', 'row'];
            if (!validDirections.includes(metadata.direction)) {
                errors.push({
                    path: `${basePath}.direction`,
                    message: `Invalid direction: ${metadata.direction}`,
                    suggestion: `Use one of: ${validDirections.join(', ')}`,
                    severity: 'error',
                });
            }
        }

        if (
            metadata.spacing !== undefined &&
            typeof metadata.spacing !== 'number'
        ) {
            errors.push({
                path: `${basePath}.spacing`,
                message: 'spacing must be a number',
                severity: 'error',
            });
        }

        if (metadata.alignment !== undefined) {
            const validAlignments = ['leading', 'trailing', 'center'];
            if (!validAlignments.includes(metadata.alignment)) {
                errors.push({
                    path: `${basePath}.alignment`,
                    message: `Invalid alignment: ${metadata.alignment}`,
                    suggestion: `Use one of: ${validAlignments.join(', ')}`,
                    severity: 'error',
                });
            }
        }
    }

    /**
     * Validates artwork selector metadata based on PEUIElementArtworkSelectorMetadata
     */
    private validateArtworkSelectorMetadata(
        metadata: any,
        basePath: string,
        errors: ValidationError[],
        warnings: ValidationError[],
    ): void {
        // Required fields
        const requiredNumericFields = [
            'width',
            'height',
            'verticalOffset',
            'horizontalOffset',
            'cornerRadius',
        ];
        requiredNumericFields.forEach((field) => {
            if (typeof metadata[field] !== 'number') {
                errors.push({
                    path: `${basePath}.${field}`,
                    message: `${field} is required and must be a number`,
                    severity: 'error',
                });
            }
        });

        if (!Array.isArray(metadata.flowIds)) {
            errors.push({
                path: `${basePath}.flowIds`,
                message: 'flowIds is required and must be an array of numbers',
                severity: 'error',
            });
        }
        // Optional fields
        if (
            metadata.source !== undefined &&
            typeof metadata.source !== 'string'
        ) {
            errors.push({
                path: `${basePath}.source`,
                message: 'source must be a string',
                severity: 'error',
            });
        }

        if (metadata.optionShape !== undefined) {
            const validShapes = ['circle', 'roundedRectangle', 'rectangle'];
            if (!validShapes.includes(metadata.optionShape)) {
                errors.push({
                    path: `${basePath}.optionShape`,
                    message: `Invalid optionShape: ${metadata.optionShape}`,
                    suggestion: `Use one of: ${validShapes.join(', ')}`,
                    severity: 'error',
                });
            }
        }

        const optionalNumericFields = [
            'optionBorderWidth',
            'optionWidth',
            'optionHeight',
        ];
        optionalNumericFields.forEach((field) => {
            if (
                metadata[field] !== undefined &&
                typeof metadata[field] !== 'number'
            ) {
                errors.push({
                    path: `${basePath}.${field}`,
                    message: `${field} must be a number`,
                    severity: 'error',
                });
            }
        });

        if (
            metadata.optionBorderColor !== undefined &&
            typeof metadata.optionBorderColor !== 'string'
        ) {
            errors.push({
                path: `${basePath}.optionBorderColor`,
                message: 'optionBorderColor must be a string',
                severity: 'error',
            });
        }
    }

    /**
     * Validates code scanner metadata based on PEUIElementCodeScannerMetadata
     */
    private validateCodeScannerMetadata(
        metadata: any,
        basePath: string,
        errors: ValidationError[],
        warnings: ValidationError[],
    ): void {
        // Code scanner metadata only requires type field, which is already validated
        // No additional validation needed based on the Swift implementation
    }

    /**
     * Validates phone keypad metadata based on PEUIPhoneKeypadMetadata
     */
    private validatePhoneKeypadMetadata(
        metadata: any,
        basePath: string,
        errors: ValidationError[],
        warnings: ValidationError[],
    ): void {
        // Required field
        if (typeof metadata.key !== 'string') {
            errors.push({
                path: `${basePath}.key`,
                message: 'key is required and must be a string',
                severity: 'error',
            });
        }

        // Optional numeric fields
        const optionalNumericFields = [
            'width',
            'height',
            'verticalOffset',
            'horizontalOffset',
            'fontSize',
            'fontWeight',
            'buttonBorderWidth',
            'buttonBorderRadius',
            'buttonFontSize',
            'buttonFontWeight',
            'buttonHorizontalPadding',
            'buttonVerticalPadding',
        ];
        optionalNumericFields.forEach((field) => {
            if (
                metadata[field] !== undefined &&
                typeof metadata[field] !== 'number'
            ) {
                errors.push({
                    path: `${basePath}.${field}`,
                    message: `${field} must be a number`,
                    severity: 'error',
                });
            }
        });

        // Optional string fields
        const optionalStringFields = [
            'fontFace',
            'buttonBorderColor',
            'buttonFontFace',
            'buttonBackgroundColor',
            'buttonTextColor',
        ];
        optionalStringFields.forEach((field) => {
            if (
                metadata[field] !== undefined &&
                typeof metadata[field] !== 'string'
            ) {
                errors.push({
                    path: `${basePath}.${field}`,
                    message: `${field} must be a string`,
                    severity: 'error',
                });
            }
        });

        // Validate buttonShape
        if (metadata.buttonShape !== undefined) {
            const validShapes = ['circle', 'roundedRectangle', 'rectangle'];
            if (!validShapes.includes(metadata.buttonShape)) {
                errors.push({
                    path: `${basePath}.buttonShape`,
                    message: `Invalid buttonShape: ${metadata.buttonShape}`,
                    suggestion: `Use one of: ${validShapes.join(', ')}`,
                    severity: 'error',
                });
            }
        }

        // Validate color formats
        const colorFields = [
            'buttonBorderColor',
            'buttonBackgroundColor',
            'buttonTextColor',
        ];
        colorFields.forEach((field) => {
            if (
                metadata[field] &&
                typeof metadata[field] === 'string' &&
                !metadata[field].match(/^#[0-9A-Fa-f]{6}$/)
            ) {
                warnings.push({
                    path: `${basePath}.${field}`,
                    message: `${field} should be a valid hex color (e.g., #FFFFFF)`,
                    severity: 'warning',
                });
            }
        });
    }

    /**
     * Validates processing flows
     */
    private validateProcessingFlows(
        flows: any,
        errors: ValidationError[],
        warnings: ValidationError[],
    ): void {
        if (typeof flows !== 'object' || flows === null) {
            errors.push({
                path: 'processingFlows',
                message: 'processingFlows must be an object',
                severity: 'error',
            });
            return;
        }

        Object.keys(flows).forEach((flowId) => {
            if (!/^\d+$/.test(flowId)) {
                errors.push({
                    path: `processingFlows.${flowId}`,
                    message: 'Processing flow keys must be numeric strings',
                    suggestion: 'Use numeric keys like "1", "2", etc.',
                    severity: 'error',
                });
            }

            if (!Array.isArray(flows[flowId])) {
                errors.push({
                    path: `processingFlows.${flowId}`,
                    message: 'Processing flow must be an array',
                    severity: 'error',
                });
                return;
            }

            // Validate each processor in the flow
            flows[flowId].forEach((processor: any, index: number) => {
                if (typeof processor !== 'object' || processor === null) {
                    errors.push({
                        path: `processingFlows.${flowId}[${index}]`,
                        message: 'Processor must be an object',
                        severity: 'error',
                    });
                    return;
                }

                // Validate processor arguments - must be string key-value pairs
                if (
                    processor.arguments &&
                    typeof processor.arguments === 'object'
                ) {
                    Object.keys(processor.arguments).forEach((argKey) => {
                        const argValue = processor.arguments[argKey];

                        // Check if argument key is a string
                        if (typeof argKey !== 'string') {
                            errors.push({
                                path: `processingFlows.${flowId}[${index}].arguments.${argKey}`,
                                message:
                                    'Processor argument keys must be strings',
                                severity: 'error',
                            });
                        }

                        // Check if argument value is a string
                        if (typeof argValue !== 'string') {
                            errors.push({
                                path: `processingFlows.${flowId}[${index}].arguments.${argKey}`,
                                message: `Processor argument values must be strings. Found: ${typeof argValue}`,
                                suggestion: `Convert "${argValue}" to string: "${String(
                                    argValue,
                                )}"`,
                                severity: 'error',
                            });
                        }
                    });
                }
            });
        });
    }

    /**
     * Validates sharing stations
     */
    private validateSharing(
        sharing: any[],
        errors: ValidationError[],
        warnings: ValidationError[],
    ): void {
        sharing.forEach((station, index) => {
            const basePath = `sharing[${index}]`;

            if (typeof station.name !== 'string') {
                errors.push({
                    path: `${basePath}.name`,
                    message:
                        'Sharing station name is required and must be a string',
                    severity: 'error',
                });
            }

            if (!Array.isArray(station.screens)) {
                errors.push({
                    path: `${basePath}.screens`,
                    message: 'Sharing station screens must be an array',
                    severity: 'error',
                });
            } else {
                this.validateScreens(station.screens, errors, warnings);
            }
        });
    }
}
